<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0"
  >
    <div class="rounded-t bg-white mb-0 px-6 py-6">
      <div class="text-center flex justify-between">
        <h6 class="text-blueGray-700 text-xl font-bold">
          <router-link
            :to="backLinkRoute()"
            class="mr-1"
            v-if="backLink || resource"
          >
            <span class="sr-only">{{ $t("button.back") }}</span>
            <i class="fas fa-arrow-left"></i>
          </router-link>
          {{ $t(title) }}
        </h6>
        <div v-if="!readonly">
          <slot name="actions" v-if="readonlyMode"></slot>
          <button
            class="text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
            :class="[readonlyMode ? 'bg-emerald-500' : 'bg-blueGray-500']"
            type="button"
            @click="toggleEdit()"
          >
            <span v-if="readonlyMode">{{ $t("button.edit") }}</span>
            <template v-if="!readonlyMode">
              <span class="sr-only">{{ $t("button.cancel") }}</span>
              <i class="fas fa-times"></i>
            </template>
          </button>
          <button
            v-if="readonlyMode"
            class="bg-red-500 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
            type="button"
            @click="deleteRecord()"
          >
            {{ $t("button.delete") }}
          </button>
          <router-link
            :to="'/' + resource + '/new'"
            v-if="readonlyMode"
            class="bg-blueGray-500 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
          >
            {{ $t("button.createOther") }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="flex-auto px-4 lg:px-10 py-10 pt-0">
      <form @submit="handleSubmit" v-if="!loading">
        <div class="flex flex-wrap mt-6">
          <div
            v-for="column in columnsToDisplay"
            :key="column.name"
            class="w-full lg:w-6/12 px-4"
            v-show="
              (!readonlyMode || !column.hideOnShow) &&
              shouldDisplayColumn(column)
            "
          >
            <template
              v-if="
                (!readonlyMode || !column.hideOnShow) &&
                shouldDisplayColumn(column)
              "
            >
              <div class="relative w-full my-4">
                <label
                  class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                >
                  {{ $t("fields." + (typeof column.label === 'function' ? column.label(localRecord) : column.label)) }}
                  <span
                    v-if="isRequired(column)"
                    :title="$t('Este campo es requerido')"
                  >
                    <span class="sr-only">
                      {{ $t("Este campo es requerido") }}</span
                    >
                    <i class="fas fa-asterisk"></i>
                  </span>
                </label>

                <!-- Readonly mode -->
                <template
                  v-if="
                    (readonlyMode || isColumnReadonly(column)) &&
                    [
                      'STRING',
                      'LONGSTRING',
                      'PASSWORD',
                      'DATE',
                      'INTEGER',
                      'DECIMAL',
                    ].includes(column.type)
                  "
                >
                  <p
                    class="border border-gray-300 bg-gray-100 px-3 py-3 min-h-12 rounded-md text-sm text-gray-700 placeholder-gray-400"
                  >
                    {{ parseContent(localRecord, column) }}
                  </p>
                </template>

                <!-- type STRING -->
                <template v-else-if="column.type === 'STRING'">
                  <input
                    type="text"
                    :required="isRequired(column)"
                    :value="localRecord[column.name]"
                    @input="updateRecordValue(column, $event.target.value)"
                    class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  />
                </template>

                <!-- type LONGSTRING -->
                <template v-else-if="column.type === 'LONGSTRING'">
                  <textarea
                    :required="isRequired(column)"
                    :value="localRecord[column.name]"
                    @input="updateRecordValue(column, $event.target.value)"
                    class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  >
                  </textarea>
                </template>

                <!-- type PASSWORD -->
                <template v-else-if="column.type === 'PASSWORD'">
                  <input
                    type="password"
                    autocomplete="new-password"
                    :required="isRequired(column)"
                    :value="localRecord[column.name] || ''"
                    @input="updateRecordValue(column, $event.target.value)"
                    class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  />
                  <PasswordChecklist
                    :password="localRecord[column.name] || ''"
                    :context="passwordContext"
                  />
                  <template v-if="localRecord[column.name]">
                    <label
                      class="block uppercase text-blueGray-600 text-xs font-bold mb-2 mt-4"
                    >
                      {{ $t("password.repeat") }}
                    </label>
                    <input
                      type="password"
                      autocomplete="new-password"
                      required
                      :value="passwordConfirmations[column.name] || ''"
                      @input="
                        passwordConfirmations[column.name] = $event.target.value
                      "
                      class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    />
                    <p
                      class="mt-2 text-xs text-red-500"
                      v-if="
                        passwordConfirmations[column.name] &&
                        passwordConfirmations[column.name] !==
                          localRecord[column.name]
                      "
                    >
                      {{ $t("password.mismatch") }}
                    </p>
                  </template>
                </template>

                <!-- type DATE -->
                <template v-else-if="column.type === 'DATE'">
                  <input
                    type="date"
                    :required="isRequired(column)"
                    :value="localRecord[column.name]"
                    @input="updateRecordValue(column, $event.target.value)"
                    class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  />
                </template>

                <!-- type INTEGER or DECIMAL -->
                <template
                  v-else-if="['INTEGER', 'DECIMAL'].includes(column.type)"
                >
                  <input
                    type="number"
                    :required="isRequired(column)"
                    :step="
                      column.type === 'INTEGER' ? '1' : column.step || '0.25'
                    "
                    :value="localRecord[column.name]"
                    @input="updateRecordValue(column, $event.target.value)"
                    :min="column.min"
                    :max="column.max"
                    class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  />
                </template>

                <!-- type BOOLEAN -->
                <template v-else-if="column.type === 'BOOLEAN'">
                  <input
                    type="checkbox"
                    :checked="localRecord[column.name]"
                    :disabled="readonlyMode || isColumnReadonly(column)"
                    @input="updateRecordValue(column, $event.target.checked)"
                    class="px-3 py-3 placeholder-blueGray-300 text-blueGray-600 rounded text-sm focus:outline-none focus:ring ease-linear transition-all duration-150"
                    :class="
                      readonlyMode || isColumnReadonly(column)
                        ? 'bg-gray-100 border border-gray-300'
                        : 'border-0 bg-white shadow'
                    "
                  />
                </template>

                <template v-else>
                  <select-join
                    :column="column"
                    :record="localRecord"
                    @pickValue="updateRecordValue(column, $event)"
                    :disabled="readonlyMode || isColumnReadonly(column)"
                  />
                </template>
              </div>
            </template>
          </div>
          <slot name="afterColumns"></slot>
          <div class="w-full px-4 mt-4 text-right" v-if="!readonlyMode">
            <button
              class="bg-blueGray-800 text-white bg-emerald-500 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
              type="submit"
              :disabled="waitingForSubmit"
            >
              {{ $t("button.save") }}
            </button>
            <button
              @click="toggleEdit()"
              class="bg-blueGray-800 text-white bg-blueGray-500 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
              type="button"
              :disabled="waitingForSubmit"
            >
              {{ $t("button.cancel") }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import SelectJoin from "@/components/Forms/SelectJoin.vue";
import PasswordChecklist from "@/components/Forms/PasswordChecklist.vue";
import { isPasswordValid } from "@/utils/passwordPolicy";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      waitingForSubmit: false,
      readonlyMode: true,
      originalRecord: {},
      localRecord: {},
      passwordConfirmations: {},
    };
  },

  components: {
    SelectJoin,
    PasswordChecklist,
  },

  props: {
    title: {},
    resource: {},
    primaryKey: {},
    columns: {},
    readonly: {},
    record: {},
    service: {},
    backLink: {},
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.auth.user,
    }),

    roles() {
      return this.userInfo?.roles || [];
    },

    loading() {
      return Object.values(this.localRecord).length === 0;
    },

    isNewRecord() {
      // record puede quedar en null (por ejemplo al cerrar sesión, cuando el
      // store se vacía antes de que la vista se desmonte)
      return !this.record?.[this.primaryKey];
    },

    columnsToDisplay() {
      return this.columns.filter((column) => this.checkRolesForColumn(column));
    },

    passwordColumns() {
      return this.columnsToDisplay.filter(
        (column) => column.type === "PASSWORD" && !column.readonly
      );
    },

    // Datos del registro que no pueden formar parte de la contraseña
    passwordContext() {
      return {
        nombre: this.localRecord.nombre,
        mail: this.localRecord.mail,
      };
    },
  },

  watch: {
    isNewRecord: {
      immediate: true,
      handler() {
        this.readonlyMode = !this.isNewRecord;
      },
    },
    record: {
      immediate: true,
      handler() {
        this.localRecord = { ...this.record };
        this.passwordConfirmations = {};

        // Pre-fill from URL defaults when creating new record
        if (this.isNewRecord && this.$route.query.defaults) {
          try {
            const defaults = JSON.parse(this.$route.query.defaults);
            Object.keys(defaults).forEach((key) => {
              const filter = defaults[key];
              if (filter && filter.value && !this.localRecord[key]) {
                this.localRecord[key] = filter.value;
              }
            });
          } catch (e) {
            console.error("Error parsing defaults:", e);
          }
        }

        this.readonlyMode = !this.isNewRecord;
      },
    },
  },

  methods: {
    backLinkRoute() {      
      if (this.backLink) {
        return typeof this.backLink === "function"
          ? this.backLink(this.localRecord)
          : this.backLink;
      } else if (this.resource) {
        return `/${this.resource}`;
      }
      return null;
    },

    toggleEdit() {
      this.readonlyMode = !this.readonlyMode;
      this.passwordConfirmations = {};

      if (this.readonlyMode) {
        Object.assign(this.originalRecord, this.record);
        this.localRecord = { ...this.originalRecord };
        if (this.isNewRecord) {
          this.$router.push("/" + this.resource);
        }
      } else {
        this.originalRecord = this.record;
      }
    },

    deleteRecord() {
      if (
        !confirm(
          "Por favor, confirme que desea eliminar este registro. Esta operación es irreversible.",
        )
      ) {
        return;
      }
      const toast = useToast();
      this.service
        .delete(this.record)
        .then((response) => {
          if (response.status < 200 || response.status > 299) {
            throw response;
          }
          toast.success("Se ha eliminado el registro exitosamente");
          this.$router.push(`/${this.resource}`);
        })
        .catch((response) => {
          toast.error("No se ha podido eliminar el registro");
          console.error(response);
        });
    },

    shouldDisplayColumn(column) {
      if (!column.conditionalDisplay) {
        return true;
      }
      return column.conditionalDisplay(this.localRecord);
    },

    updateRecordValue(column, newValue) {
      const value = newValue === "" ? null : newValue;
      this.localRecord[column.name] = value;

      // Ejecutar onChange si existe en el modelo
      if (column.onChange) {
        column.onChange(this.localRecord);
      }
    },

    // Valida los campos de contraseña antes de enviar. El backend vuelve a
    // validar lo mismo: esto es sólo para no hacer el viaje en vano.
    validatePasswordColumns() {
      for (const column of this.passwordColumns) {
        const value = this.localRecord[column.name];

        if (!value) {
          continue;
        }

        if (!isPasswordValid(value, this.passwordContext)) {
          return this.$t("password.invalid");
        }

        if (this.passwordConfirmations[column.name] !== value) {
          return this.$t("password.mismatch");
        }
      }

      return null;
    },

    handleSubmit(event) {
      event.preventDefault();

      const toastValidation = useToast();
      const passwordError = this.validatePasswordColumns();

      if (passwordError) {
        toastValidation.error(passwordError);
        return;
      }

      this.waitingForSubmit = true;
      this.columnsToDisplay.forEach((column) => {
        if (this.localRecord[column.name]) {
          switch (column.type) {
            case "INTEGER":
              this.localRecord[column.name] = parseInt(
                this.localRecord[column.name],
              );
              break;
            case "DECIMAL":
              this.localRecord[column.name] = parseFloat(
                this.localRecord[column.name],
              );
              break;
          }
        }
      });

      const toast = useToast();
      this.service
        .upsert(this.localRecord)
        .then((data) => {
          if (!data.record || data.error) {
            throw data;
          }
          this.$emit("saveChanges", data.record);
          toast.success("Se han guardado los cambios exitosamente");
          this.$router.push(
            `/${this.resource}/${data.record[this.primaryKey]}`,
          );
        })
        .catch((response) => {
          toast.error("No se han podido guardar los cambios");
          console.error(response);
        })
        .finally(() => {
          this.waitingForSubmit = false;
        });
    },

    isRequired(column) {
      if (typeof column.allowNull === "function") {
        return !column.allowNull(this.localRecord);
      }
      return !column.allowNull;
    },

    isColumnReadonly(column) {
      if (typeof column.readonly === "function") {
        return column.readonly(this.localRecord, this.roles);
      }
      return !!column.readonly;
    },

    checkRolesForColumn(column) {
      if (!column.roles) {
        return true;
      }

      let display = false;

      for (let rol of column.roles) {
        display = display || this.roles.includes(rol);
      }

      return display;
    },

    parseContent(record, column, fullContent = true) {
      let value =
        typeof column.value === "function"
          ? column.value(record)
          : record[column.name];

      if (!value) {
        return value;
      }

      if (column.currency || column.type === "INTEGER") {
        value = new Intl.NumberFormat("es-UY", {
          maximumFractionDigits: 0,
          useGrouping: true,
        }).format(value);
      } else if (column.type == "DECIMAL") {
        value = new Intl.NumberFormat("es-UY", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true,
        }).format(value);
      } else if (column.type == "DATE") {
        const dateValue = new Date(value);
        if (isNaN(dateValue.getTime())) {
          return value;
        }
        value = new Intl.DateTimeFormat("es-UY", {
          dateStyle: "short",
          timeZone: "UTC",
        }).format(dateValue);
        if (dateValue.getHours() !== 21 || dateValue.getMinutes() !== 0) {
          value += " ";
          value += new Intl.DateTimeFormat("es-UY", {
            timeZone: "UTC",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
          }).format(dateValue);
        }
      } else if (
        ["STRING", "LONGSTRING"].includes(column.type) &&
        value.length > 32 &&
        !fullContent
      ) {
        value = value.slice(0, 32) + "...";
      }

      return value;
    },
  },
};
</script>
